// Configuration for the "הנחות" (discounts) settings tab.
// The discounts list is read-only (add/edit happens in a separate form, built
// in a follow-up slice); each company defines its own display columns.

import { COMPANY_IDS } from './companies.config';
import { SelectOption } from './simulatorSettings.constants';

export const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

// AmountType: Sum=0 (₪), Percent=1 (%)
export const AMOUNT_TYPE_OPTIONS: SelectOption[] = [
    { value: 0, label: '₪' },
    { value: 1, label: '%' },
];

// ShlomoDiscountType (numeric enum)
export const SHLOMO_DISCOUNT_TYPE_OPTIONS: SelectOption[] = [
    { value: 0, label: 'הנחת עמלה' },
    { value: 1, label: 'הנחה משותפת' },
    { value: 2, label: 'הנחה 2' },
    { value: 3, label: 'הגדלת ה"ע' },
];

export interface DiscountCompanyTab {
    key: string;
    label: string;
    id: string;
}

export const DISCOUNT_COMPANY_TABS: DiscountCompanyTab[] = [
    { key: 'ayalon', label: 'איילון', id: COMPANY_IDS.AYALON },
    { key: 'harel', label: 'הראל', id: COMPANY_IDS.HAREL },
    { key: 'shomera', label: 'שומרה', id: COMPANY_IDS.SHOMERA },
    { key: 'shlomo', label: 'שלמה', id: COMPANY_IDS.SHLOMO },
    { key: 'migdal', label: 'מגדל', id: COMPANY_IDS.MIGDAL },
    { key: 'hachshara', label: 'הכשרה', id: COMPANY_IDS.HACHSHARA },
    { key: 'clal', label: 'כלל', id: COMPANY_IDS.CLAL },
    { key: 'fnx', label: 'הפניקס', id: COMPANY_IDS.FNX },
    { key: 'menora', label: 'מנורה', id: COMPANY_IDS.MENORA },
    { key: 'ankor', label: 'אנקור', id: COMPANY_IDS.ANKOR },
];

// ShlomoDiscountType display names (numeric enum)
const SHLOMO_DISCOUNT_TYPE_LABELS: Record<number, string> = {
    0: 'הנחת עמלה',
    1: 'הנחה משותפת',
    2: 'הנחה 2',
    3: 'הגדלת ה"ע',
};

// --- display helpers (mirror the old Razor formatting) ---
const pct = (v: any) => `${v ?? 0} %`;
const yesNo = (v: any) => (v === 0 ? 'כן' : 'לא'); // YesNo: Yes=0, No=1
const inForce = (v: any) => (v === 0 ? 'בתוקף' : 'לא בתוקף');
const ageRange = (d: any) => `${d.driverAgeMin ?? ''} - ${d.driverAgeMax ?? ''}`;
const shlomoType = (v: any) => SHLOMO_DISCOUNT_TYPE_LABELS[v] ?? '';

const insuranceType = (d: any) => {
    if (d.comprehensiveInsurance && d.thirdPartyInsurance) return 'הכל';
    if (d.comprehensiveInsurance) return 'מקיף';
    if (d.thirdPartyInsurance) return "צד ג'";
    return '';
};

const vehicleType = (d: any) => {
    if (d.privateVehicle && d.commercialVehicle) return 'הכל';
    if (d.privateVehicle) return 'פרטי';
    if (d.commercialVehicle) return 'מסחרי';
    return '';
};

const shlomoCollective = (d: any) =>
    Array.isArray(d.routeDatas)
        ? d.routeDatas.map((r: any) => r.comment).filter(Boolean).join(', ')
        : '';

// AmountType: 0 = ₪ (Sum), 1 = % (Percent)
const amountWithType = (d: any) => `${d.sum ?? 0} ${d.amountType === 0 ? '₪' : '%'}`;

export interface DiscountColumn {
    label: string;
    render: (discount: any) => any;
}

// generic layout for companies without bespoke discount fields
const genericDiscountColumns: DiscountColumn[] = [
    { label: 'שם ההנחה', render: (d) => d.name },
    { label: 'גובה הנחה', render: (d) => amountWithType(d) },
    { label: 'אוטומטי?', render: (d) => yesNo(d.autoActive) },
    { label: "מס' סידורי", render: (d) => d.sequenceId },
    { label: 'טווח גילאים', render: (d) => ageRange(d) },
    { label: 'סוג ביטוח', render: (d) => insuranceType(d) },
    { label: 'סוג רכב', render: (d) => vehicleType(d) },
    { label: 'ללא עבר ביטוחי', render: (d) => inForce(d.noPassedInsurance) },
    { label: 'כולל תביעות', render: (d) => inForce(d.sueExists) },
];

export const DISCOUNT_COLUMNS: Record<string, DiscountColumn[]> = {
    [COMPANY_IDS.AYALON]: [
        { label: 'שם ההנחה', render: (d) => d.name },
        { label: 'הנחת סוכן', render: (d) => pct(d.ayalonHanachatSochen) },
        { label: 'הנחת מחשבון', render: (d) => pct(d.ayalonHanachatHatama) },
        { label: 'אוטומטי?', render: (d) => yesNo(d.autoActive) },
        { label: "מס' סידורי", render: (d) => d.sequenceId },
        { label: 'טווח גילאים', render: (d) => ageRange(d) },
        { label: 'סוג ביטוח', render: (d) => insuranceType(d) },
        { label: 'סוג רכב', render: (d) => vehicleType(d) },
        { label: 'ללא עבר ביטוחי', render: (d) => inForce(d.noPassedInsurance) },
        { label: 'כולל תביעות', render: (d) => inForce(d.sueExists) },
    ],
    [COMPANY_IDS.HAREL]: [
        { label: 'שם ההנחה', render: (d) => d.name },
        { label: 'קולקטיב', render: (d) => d.collectiveNumber },
        { label: 'גובה הנחה', render: (d) => pct(d.sum) },
        { label: 'אוטומטי?', render: (d) => yesNo(d.autoActive) },
        { label: "מס' סעיף", render: (d) => d.branchId },
        { label: "מס' סידורי", render: (d) => d.sequenceId },
        { label: 'טווח גילאים', render: (d) => ageRange(d) },
        { label: 'סוג ביטוח', render: (d) => insuranceType(d) },
        { label: 'סוג רכב', render: (d) => vehicleType(d) },
        { label: 'ללא עבר ביטוחי', render: (d) => inForce(d.noPassedInsurance) },
        { label: 'כולל תביעות', render: (d) => inForce(d.sueExists) },
    ],
    [COMPANY_IDS.SHLOMO]: [
        { label: 'שם ההנחה', render: (d) => d.name },
        { label: 'קולקטיב', render: (d) => shlomoCollective(d) },
        { label: 'סוג ההנחה', render: (d) => shlomoType(d.shlomoDiscountType) },
        { label: 'גובה הנחה', render: (d) => pct(d.sum) },
        { label: 'אוטומטי?', render: (d) => yesNo(d.autoActive) },
        { label: "מס' סידורי", render: (d) => d.sequenceId },
    ],
    [COMPANY_IDS.SHOMERA]: genericDiscountColumns,
    [COMPANY_IDS.MIGDAL]: genericDiscountColumns,
    [COMPANY_IDS.HACHSHARA]: genericDiscountColumns,
    [COMPANY_IDS.CLAL]: genericDiscountColumns,
    [COMPANY_IDS.FNX]: genericDiscountColumns,
    [COMPANY_IDS.MENORA]: genericDiscountColumns,
    [COMPANY_IDS.ANKOR]: genericDiscountColumns,
};
